import {
  assertEditContextWireIds,
  assertSendable,
  BasilSendRefusal,
  editContextPath,
} from './sendGuard';
import {
  BasilHttp,
  EditContextBody,
  EditContextReply,
  RequestDelivery,
} from './writeTypes';

/**
 * The edit-context transport (spec sections 5.4 and 5.6): ONE request, no blind retry, and a
 * result that never reads a failure as a success.
 *
 * The traps this file exists for:
 * - `components/api.js:60-72` RESOLVES a 503 with `{ data: {}, status: 503 }` after redirecting to
 *   `/maintenancepage`, so `await api.patch(...)` on a 503 looks like a success with no counts. A
 *   missing `success` or missing counts is a FAILURE here (E19).
 * - a 401 simply rejects — the redirect to `/logout` is commented out (`api.js:74-86`) — and is an
 *   abort with the reason `session-lost`.
 * - a network failure leaves the outcome UNKNOWN: the audit entry records that, and the caller
 *   re-reads before deciding anything.
 */

export type TransportFailure =
  | 'refused'
  | 'maintenance-503'
  | 'session-lost'
  | 'forbidden'
  | 'validation-422'
  | 'payload-too-large'
  | 'server-error'
  | 'missing-counts'
  | 'network-unknown';

export interface TransportResult {
  /** true only when a well-formed reply came back; reconciliation still has to agree. */
  ok: boolean;
  status: number | null;
  reply: EditContextReply | null;
  failure: TransportFailure | null;
  message: string | null;
  details: Array<{ message: string }>;
  ms: number;
  /** false when the request may have been applied but the response was lost. */
  outcomeKnown: boolean;
  /**
   * Whether the body left the browser, and whether anything answered it (T17-01).
   *
   * It is classified HERE, from the two facts only the transport can see — did this function issue
   * a request at all, and did a reply come back — and from nothing else. It is not derived from
   * `ok`, from `outcomeKnown` or from what the reply said: those are questions about the OUTCOME,
   * and the outcome being unknown is not evidence that nothing was sent.
   *
   *  * `not-sent`  the local refusal fired: no request was issued.
   *  * `sent`      the engine answered, however badly. Receipt is proven.
   *  * `unknown`   the request was dispatched and nothing came back from the engine. It may be in
   *                the episode and may not be, and nothing may claim either.
   *
   * A resolved 503 is `unknown`, not `sent`: `components/api.js` resolves the maintenance
   * interception itself, so that status is not the engine answering.
   */
  delivery: RequestDelivery;
}

interface AxiosLikeError {
  response?: {
    status?: number;
    data?: { error?: string; message?: string; details?: Array<{ message: string }> };
  };
  message?: string;
}

function readDetails(data?: { details?: Array<{ message: string }> }): Array<{ message: string }> {
  if (!data || !Array.isArray(data.details)) {
    return [];
  }

  return data.details.map((detail) => ({ message: String(detail && detail.message) }));
}

function failureForStatus(status: number): TransportFailure {
  if (status === 401) {
    return 'session-lost';
  }

  if (status === 403) {
    return 'forbidden';
  }

  if (status === 413) {
    return 'payload-too-large';
  }

  if (status === 422) {
    return 'validation-422';
  }

  if (status === 503) {
    return 'maintenance-503';
  }

  return 'server-error';
}

function hasCounts(reply: EditContextReply | null): boolean {
  if (!reply) {
    return false;
  }

  return typeof reply.updatedNodes === 'number'
    && typeof reply.updatedSteps === 'number'
    && typeof reply.updatedAnswers === 'number';
}

export interface SendEditContextArgs {
  http: BasilHttp;
  storyId: number;
  body: EditContextBody;
  /** Injected clock, so the recorded `ms` is deterministic in tests. */
  now?: () => number;
}

/**
 * Issue exactly one `PATCH /v1/stories/:id/edit-context`.
 *
 * Refuses locally — with no request issued — when the route or any id would be wrong (E2..E5, E15,
 * W1.6). Every other outcome comes back as a `TransportResult` the caller reconciles; nothing is
 * ever retried here.
 */
export async function sendEditContextChunk(args: SendEditContextArgs): Promise<TransportResult> {
  const {
    http, storyId, body,
  } = args;
  const now = args.now || (() => Date.now());
  const path = editContextPath(storyId);
  const startedAt = now();

  try {
    assertSendable('PATCH', path, body);
    assertEditContextWireIds(body);
  } catch (error) {
    const refusal = error instanceof BasilSendRefusal ? error : null;

    return {
      ok: false,
      status: null,
      reply: null,
      failure: 'refused',
      message: refusal ? refusal.message : String(error),
      details: [],
      ms: 0,
      outcomeKnown: true,
      // Nothing was issued: the refusal fired before any call was made.
      delivery: 'not-sent',
    };
  }

  try {
    const response = await http.patch<EditContextReply>(path, body);
    const ms = now() - startedAt;
    const status = typeof response.status === 'number' ? response.status : 0;
    const reply = (response.data || null) as EditContextReply | null;

    if (status === 503) {
      return {
        ok: false,
        status,
        reply,
        failure: 'maintenance-503',
        message: "Basil can't reach this episode right now, so this change isn't confirmed. Reload to see where it stands.",
        details: [],
        ms,
        outcomeKnown: false,
        // The maintenance interception answered, not the engine, so receipt is not proven.
        delivery: 'unknown',
      };
    }

    if (!reply || reply.success !== true || !hasCounts(reply)) {
      return {
        ok: false,
        status,
        reply,
        failure: 'missing-counts',
        message: "Your change was sent, but Basil couldn't confirm what happened to it.",
        details: readDetails(reply || undefined),
        ms,
        outcomeKnown: false,
        // A reply came back: receipt is proven. What it did is the part nobody knows.
        delivery: 'sent',
      };
    }

    return {
      ok: true,
      status,
      reply,
      failure: null,
      message: null,
      details: readDetails(reply),
      ms,
      outcomeKnown: true,
      delivery: 'sent',
    };
  } catch (error) {
    const ms = now() - startedAt;
    const axiosError = error as AxiosLikeError;
    const status = axiosError && axiosError.response ? axiosError.response.status : undefined;

    // A dropped connection is not an answer. The browser client in this tree (axios 0.27) builds
    // its network error with the request object in the response seat, so `response.status` reads
    // the request's own status, which is 0 when nothing came back. Status 0 is therefore the same
    // fact as no status at all: dispatched, nothing heard. Seen on the served build, 2026-09-14.
    if (typeof status !== 'number' || status === 0) {
      return {
        ok: false,
        status: null,
        reply: null,
        failure: 'network-unknown',
        message: (axiosError && axiosError.message) || "The connection dropped before Basil heard back, so this change isn't confirmed.",
        details: [],
        ms,
        outcomeKnown: false,
        // Dispatched, and nothing came back from the engine. Neither claim is available.
        delivery: 'unknown',
      };
    }

    const data = axiosError.response ? axiosError.response.data : undefined;

    return {
      ok: false,
      status,
      reply: null,
      failure: failureForStatus(status),
      message: (data && (data.error || data.message)) || 'Something went wrong sending this change. Reload to see where it stands.',
      details: readDetails(data),
      ms,
      outcomeKnown: status === 422 || status === 403 || status === 413,
      // The engine answered with an error, and an error is still an answer — except the 503 the
      // maintenance interception produces, which is not the engine's.
      delivery: status === 503 ? 'unknown' : 'sent',
    };
  }
}
